import { AnyAction } from 'redux'
import { camelize, snakeize } from 'casing'
import { isEmpty, toLower } from 'lodash'
import { AxiosError, AxiosResponse } from 'axios'
import {
  errorLogger,
  getErrorMessages,
  isNonEmptyNumber,
  LoadingTypes,
  startLoading,
  stopLoading,
  updateAppAlert,
  emitSalesforceResourceSaved,
} from '@labrav/common'
import { PlanType } from '@labrav/react-components'

import type { AppDispatch, RootState } from '../../store/types'
import type { CloudMarketplace } from '../../types/index'
import {
  AgreementDurationType,
  DiscountType,
  EulaType,
  ResellerAgreementStatus,
  type Dimension,
  type FieldMetadata,
  type Installments,
} from '../../types/flyout'
import type { Product, ProductDimension } from '../productsListing/types'
import type { ResaleAuthorizationsResellerAgreements } from '../../types/resaleAuthorization'
import {
  ResaleAuthorizationCreationActionTypes,
  type KeyValue,
  type ResaleAuthorizationWithErrors,
  type setResaleAuthorizationInCreationProps,
} from './types'
import { CRMTypes, defaultCrmType } from '../../utils/constants'
import {
  cloneResaleAuthSuccess,
  deactivateResaleAuthSuccess,
  RequestFailureMessage,
  resaleAuthorizationCreationSuccess,
  resaleAuthorizationSaveSuccess,
} from '../../common/utils/messagesContants'
import {
  patchResellerData,
  postResaleAuthorization,
} from '../../api/marketplace/index'
import { getAllPartnerUserEmails } from '../../api/partnerUsers'
import { getMarketplaceDefaultOfferMarketplaceMapping } from '../../api/mappingManager/index'
import { NET_PAYMENT_TERMS_AWS_DEFAULT } from '../privateOffer/privateOfferCreation/utils/helperFunctions'
import { createOfferErrors } from '../privateOffer/privateOfferCreation/action'
import { getFilteredInstallments } from '../privateOffer/privateOfferCreation/utils/installmentHelper'
import { mapObjectPropertyEmptyToNull } from '../channelPartnerPrivateOffer/utils/mapObjectPropertyEmptyToNull'

import {
  getPricesArray,
  arrayFieldsForValidations,
} from './utils/getPricesArray'
import { normalizeResaleAuthorizationForForm } from './utils/normalizeResaleAuthorizationForForm'
import { getInitialResaleAuthorizationData } from './utils/getInitialResaleAuthorizationData'
import { getEditResaleAuthorizationData } from './utils/getEditResaleAuthorizationData'
import { getResellerProductDimensions } from './utils/dimensionPayloadHelpers'
import {
  mapResellersInfo,
  returnNecessaryFieldResaleAuthorization,
  getSubmitPayloadResaleAuthorization,
} from './utils/sendPayloadHelpers'
import {
  transformResaleAuthForSave,
  cloneAPIresaleAuthorization,
  submitAPIresaleAuthorization,
  patchAPIresaleAuthorization,
} from './utils/cloneSaveHelpers'
import {
  mappingReferralObjectToForm,
  getFieldMetadataFromTemplate,
} from './utils/mappingHelpers'
import { getCurrentResaleAuthorization } from './utils/getCurrentResaleAuthorization'

export { ResaleAuthorizationCreationActionTypes } from './types'
export type { setResaleAuthorizationInCreationProps } from './types'
export {
  getPricesArray,
  arrayFieldsForValidations,
} from './utils/getPricesArray'
export { normalizeResaleAuthorizationForForm } from './utils/normalizeResaleAuthorizationForForm'
export { getAzureMappedPlans } from '../../utils/getAzureMappedPlans'
export {
  getDiscountedPrice,
  getResalePriceKeySnakeizConversion,
  getResalePriceKeyCamelizeConversion,
} from './utils/priceKeyConversion'
export {
  getPricesOrPriceOrQuantity,
  getResalePricesOrPriceOrQuantity,
  getResellerProductDimensions,
  getUpdatedResellerProductDimensions,
} from './utils/dimensionPayloadHelpers'
export {
  cloneSaveData,
  transformResaleAuthForSave,
  patchAPIresaleAuthorization,
} from './utils/cloneSaveHelpers'
export { processMeters } from './utils/processMeters'
export {
  mappingReferralObjectToForm,
  awsReferralFieldToFormMetadataKey,
  referralFieldToFormMetadataKey,
  azureReferralFieldToFormMetadataKey,
  getOfferDetailInfo,
  getFieldMetadataFromTemplate,
} from './utils/mappingHelpers'
export {
  reminderResaleAuthorization,
  editFailedResaleAuthorizationMPPO,
  deleteCurrentResaleAuthorization,
} from './utils/detailPageActions'

const validateFields = [
  'companyName',
  'agreementName',
  'agreementDescription',
  'agreementRecipients',
  'metaData',
  'emailCustomMessage',
  'buyerAwsAccountId',
  'percentageDiscount',
  'installmentInfo',
  'dimensions',
  'resellersInfo',
  'discountType',
  'contractDurationType',
  'contractDurationInMonthsForEndCustomer',
  'agreementExpirationDate',
]

export const getCurrentResaleAuthorizationInCreation =
  ({
    partnerId,
    productId,
    resaleAuthorizationId,
    crmOpportunityId,
    cloud,
    labraNewUiRevamp,
  }: {
    partnerId: string
    productId: string
    resaleAuthorizationId: string
    crmOpportunityId?: string
    cloud?: CloudMarketplace
    labraNewUiRevamp?: boolean
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloudMarketplace = cloud || 'AWS'

    const product =
      getState().productsListing[cloudMarketplace].products.rows.find(
        product => product.productId === productId
      ) || ({} as Product)

    const userProfile = getState().userProfile.userProfile

    let offerData = {}
    let errors = {}
    if (isEmpty(resaleAuthorizationId)) {
      offerData = getInitialResaleAuthorizationData({
        product,
        cloud: cloudMarketplace,
        crmOpportunityId,
        userProfile,
        getState,
        labraNewUiRevamp,
      })
    } else {
      const { userList } = getState().userList.user
      const usersEmails = await getAllPartnerUserEmails(partnerId)
      offerData = await getEditResaleAuthorizationData({
        partnerId,
        product,
        resaleAuthorizationId,
        cloud: cloudMarketplace,
        crmOpportunityId,
        userList: userList.users,
        usersEmails,
        getState,
      })
    }

    //this block should be remove when rewamp the AWS resale auth
    if (cloudMarketplace === 'AWS') {
      const pricesArray: string[] = getPricesArray(
        product?.productDimensions as Dimension[]
      )

      offerData = normalizeResaleAuthorizationForForm(
        {
          ...camelize(offerData),
          pricesArray,
          ...(labraNewUiRevamp
            ? {
                contract_duration_time_unit: 'MONTHS',
              }
            : {}),
        },
        { labraNewUiRevamp }
      )
      const { resellersInfo, ...errorsData } = createOfferErrors(
        offerData,
        arrayFieldsForValidations,
        true
      )
      errors = errorsData
    }
    dispatch(
      setResaleAuthorizationInCreation({
        productId,
        data:
          cloudMarketplace === 'AWS'
            ? { ...camelize(offerData) }
            : { ...offerData },
        errors: errors || {},
        noOfTimesSaved: isEmpty(resaleAuthorizationId) ? 0 : 2,
      })
    )
  }

export const setResaleAuthorizationInCreation = ({
  productId,
  data,
  errors,
  noOfTimesSaved = 0,
}: setResaleAuthorizationInCreationProps) =>
  ({
    type: ResaleAuthorizationCreationActionTypes.SET_CREATE_RESALE_AUTHORIZATIONS_DATA,
    payload: {
      productId,
      resaleAuthorizationData: data,
      errors,
      noOfTimesSaved,
    },
  }) as unknown as AnyAction

export const setErrorsOnResaleAuthorizationCreation = (
  key: string,
  productId: string,
  field?: string,
  index?: number,
  standardDimensions?: ProductDimension[]
) => ({
  type: ResaleAuthorizationCreationActionTypes.UPDATE_RESALE_AUTHORIZATION_CREATION_ERRORS,
  payload: { key, productId, field, index, standardDimensions },
})

export const setValuesInArrayOfResaleAuthorizations = (
  key: string,
  index: number,
  resaleAuthorizationData: KeyValue,
  productId: string
) => ({
  type: ResaleAuthorizationCreationActionTypes.UPDATE_VALUES_OF_ARRAY_ON_RESALE_AUTHORIZATION,
  payload: { key, resaleAuthorizationData, index, productId },
})

export const clearResaleAuthorizationInCreation = (productId: string) => ({
  type: ResaleAuthorizationCreationActionTypes.CLEAR_RESALE_AUTHORIZATION_IN_CREATION,
  payload: productId,
})

export const addRowsOnResaleAuthorization = (
  key: string,
  productId: string,
  type = 'standard'
) => ({
  type: ResaleAuthorizationCreationActionTypes.ADD_ROWS_ON_RESALE_AUTHORIZATION,
  payload: { key, productId, type },
})
export const deleteRowsOnResaleAuthorization =
  (key: string, index: number, productId: string) =>
  async (dispatch: AppDispatch) => {
    await dispatch(deleteRows(key, index, productId))
    if (validateFields.includes(key)) {
      await dispatch(setErrorsOnResaleAuthorizationCreation(key, productId))
    }
  }
export const deleteRows = (key: string, index: number, productId: string) => ({
  type: ResaleAuthorizationCreationActionTypes.DELETE_ROWS_ON_RESALE_AUTHORIZATION,
  payload: { key, index, productId },
})

export const updatevaluesInArrayOfResaleAuthorizations =
  (
    key: string,
    index: number,
    resaleAuthorizationData: KeyValue,
    productId: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState()
    const cloudType = state.productListingFilters.cloudMarketplace
    const standardDimensions = state.productsListing[
      cloudType
    ].products.rows.filter(val => val.productId === productId)[0]
      .productDimensions
    await dispatch(
      setValuesInArrayOfResaleAuthorizations(
        key,
        index,
        resaleAuthorizationData,
        productId
      ) as unknown as AnyAction
    )
    if (validateFields.includes(key)) {
      await dispatch(
        setErrorsOnResaleAuthorizationCreation(
          key,
          productId,
          resaleAuthorizationData.key,
          index,
          standardDimensions
        ) as unknown as AnyAction
      )
    }
  }
export const updateResaleAuthorizationInCreation =
  (data: KeyValue, productId: string) => async (dispatch: AppDispatch) => {
    await dispatch(setResaleAuthorizationsData(data, productId))
    if (validateFields.includes(data.key)) {
      await dispatch(
        setErrorsOnResaleAuthorizationCreation(data.key as string, productId)
      )
    }
  }

export const setResaleAuthorizationsData = (
  data: KeyValue,
  productId: string
) => ({
  type: ResaleAuthorizationCreationActionTypes.UPDATE_RESALE_AUTHORIZATIONS,
  payload: { resaleAuthorizationData: data, productId },
})

export const updateDimensionOnResale = (
  index: number,
  productId: string,
  dimension: Dimension
) => ({
  type: ResaleAuthorizationCreationActionTypes.UPDATE_DIMENSION_ON_RESALE_AUTHORIZATION,
  payload: { productId, dimension, index },
})

export const updatePricesOnResaleDimensions =
  (productId: string, index: number, priceData: KeyValue) =>
  async (dispatch: AppDispatch) => {
    await dispatch(setPricesOnResale(productId, index, priceData))
    await dispatch(
      setErrorsOnResaleAuthorizationCreation(
        'dimensions',
        productId,
        priceData.key,
        index
      ) as unknown as AnyAction
    )
  }

export const setPricesOnResale = (
  productId: string,
  index: number,
  priceData: KeyValue
) => ({
  type: ResaleAuthorizationCreationActionTypes.UPDATE_PRICES_ON_RESALE_AUTHORIZATION_DIMENSIONS,
  payload: { productId, index, priceData },
})
export const updateHasBeenSent = (hasBeenSent: boolean, productId: string) => ({
  type: ResaleAuthorizationCreationActionTypes.UPDTE_RESALE_AUTHORIZATION_HAS_BEEN_SENT,
  payload: { hasBeenSent, productId },
})

export const updateNoOfTimesSaved = (productId: string) => ({
  type: ResaleAuthorizationCreationActionTypes.UPDATE_NUMBER_OF_TIMES_SAVED_ON_RESALE_AUTHORIZATION,
  payload: productId,
})

export const sendResaleAuthorization =
  ({
    partnerId,
    productId,
    saveAsDraft,
    crmOpportunityId,
    crmObjectLink,
    creatorData,
  }: {
    partnerId: string
    productId: string
    saveAsDraft?: boolean
    crmOpportunityId?: string
    crmObjectLink?: string
    creatorData?: {
      creatorEmail?: string
      creatorName?: string
    }
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      //TODO LT-5308: When Azure enable for resale then we have to do dynamic
      const cloudMarketplace = 'AWS' //getState().productListingFilters.cloudMarketplace
      const product =
        getState().productsListing[cloudMarketplace].products?.rows.find(
          product => product.productId === productId
        ) || ({} as Product)

      const resaleAuthorizationState =
        getState().resaleAuthorizationsInCreation
          .currentResaleAuthorizationInCreation[productId]

      const {
        discountType,
        agreementDurationType,
        resellerLicenseAgreementType,
        percentageDiscount,
        agreementExpirationDate,
        buyerAwsAccountId,
        awsAgreementId,
        resellersInfo,
        contractDurationType,
        contractDurationInMonthsForEndCustomer,
        companyName,
        ...resaleAuthorization
      } = returnNecessaryFieldResaleAuthorization(resaleAuthorizationState)
      const {
        givenName: firstName,
        familyName: lastName,
        email,
      } = getState().userProfile.userProfile
      const hasCreatorData = !isEmpty(firstName) && !isEmpty(email)
      const creatorDataToBeSent = creatorData
        ? snakeize({ creatorData: creatorData })
        : hasCreatorData
          ? {
              creator_data: { creator_name: firstName, creator_email: email },
            }
          : {}
      const authorizationStakeholders = [
        {
          firstName: firstName || '',
          lastName: lastName || '',
          email: email || '',
        },
      ]
      const resaleAuthorizationToBeSent = { ...resaleAuthorization } as Record<
        string,
        unknown
      >
      let crmMetadata = {} as Record<string, unknown>
      if (crmOpportunityId && crmObjectLink) {
        crmMetadata = {
          crmType: defaultCrmType,
          crmObjectLink,
        }
      }
      resaleAuthorizationToBeSent.discountType = discountType
        ? toLower(discountType)
        : null

      const convertedDiscountType =
        product.productType === PlanType.SAAS_SUBSCRIPTION
          ? DiscountType.INDIVIDUAL_PRICING
          : discountType

      resaleAuthorizationToBeSent.agreementDurationType =
        product.productType === PlanType.SAAS_CONTRACT &&
        convertedDiscountType === DiscountType.FLEXIBLE_PAYMENT_TERMS
          ? toLower(AgreementDurationType.SINGLE_USE)
          : toLower(agreementDurationType)

      resaleAuthorizationToBeSent.resellerLicenseAgreementType =
        toLower(resellerLicenseAgreementType) || null

      resaleAuthorizationToBeSent.draft = !!saveAsDraft

      resaleAuthorizationToBeSent.percentageDiscount = percentageDiscount
        ? percentageDiscount
        : null

      resaleAuthorizationToBeSent.contractDurationInMonthsForEndCustomer =
        isNonEmptyNumber(contractDurationInMonthsForEndCustomer)
          ? contractDurationInMonthsForEndCustomer
          : null

      const updatedResaleAuthorization = {
        ...resaleAuthorizationToBeSent,
        ...mapObjectPropertyEmptyToNull({
          agreementExpirationDate,
          buyerAwsAccountId,
          awsAgreementId,
          contractDurationType,
          companyName,
        }),
      }
      const { id, ...resaleAuthorizationToBeSentWithoutId } =
        updatedResaleAuthorization

      resaleAuthorizationToBeSentWithoutId.installmentInfo =
        getFilteredInstallments(
          resaleAuthorizationToBeSentWithoutId.installmentInfo as unknown as Installments[]
        )
      resaleAuthorizationToBeSentWithoutId.agreementStakeholders = [
        ...authorizationStakeholders,
      ]
      resaleAuthorizationToBeSentWithoutId.endCustomerEulaFiles =
        resaleAuthorization?.endCustomerEulaType !== EulaType.CUSTOM
          ? []
          : resaleAuthorization?.endCustomerEulaFiles
      resaleAuthorizationToBeSentWithoutId.resellerLicenseAgreementFiles =
        resellerLicenseAgreementType !== EulaType.CUSTOM
          ? []
          : resaleAuthorization?.resellerLicenseAgreementFiles

      const awsProductId = product?.productDimensions
        ? product.productDimensions[0].awsProductId || product.productId
        : product.productId
      const dataToBeSent = {
        ...snakeize({
          ...resaleAuthorizationToBeSentWithoutId,
          netPaymentTerms:
            isEmpty(resaleAuthorizationState?.netPaymentTerms) ||
            resaleAuthorizationState?.netPaymentTerms ===
              NET_PAYMENT_TERMS_AWS_DEFAULT
              ? null
              : resaleAuthorizationState.netPaymentTerms,
          ...(crmOpportunityId ? { crmMetadata } : {}),
          ...mapResellersInfo({
            resellersInfo,
          }),
          awsProductId,
        }),
        products: getResellerProductDimensions({
          resaleAuthorizationState,
          product,
        }),
      }
      if (isEmpty(id)) {
        const { data } = await postResaleAuthorization({
          partnerId,
          data: {
            ...dataToBeSent,
            ...creatorDataToBeSent,
          },
          saveAsDraft,
        })
        await dispatch(
          updateResaleAuthorizationInCreation(
            { key: 'id', value: camelize(data).labraAwsResellerAgreementId },
            productId
          ) as unknown as AnyAction
        )
      } else {
        await patchResellerData(
          partnerId,
          id as string,
          { ...dataToBeSent, ...creatorDataToBeSent },
          saveAsDraft
        )
      }
      if (!saveAsDraft) {
        await dispatch(updateHasBeenSent(true, productId))
      } else {
        await dispatch(updateNoOfTimesSaved(productId))
      }
      dispatch(
        updateAppAlert({
          message: saveAsDraft
            ? resaleAuthorizationSaveSuccess
            : resaleAuthorizationCreationSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )

      emitSalesforceResourceSaved({
        resourceType: 'reseller-auth',
        partnerId,
        opportunityId: crmOpportunityId || null,
        resellerAuthorization: {
          id: resaleAuthorization?.id,
          draft: !!saveAsDraft,
          hasBeenSent: !saveAsDraft,
          productId,
        },
      })
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const addFilesOnResaleAuthorization = (
  productId: string,
  files: string[],
  key: 'endCustomerEulaFiles' | 'resellerLicenseAgreementFiles'
) => ({
  type: ResaleAuthorizationCreationActionTypes.ADD_FILES_ON_RESALE_AUTHORIZATION,
  payload: { productId, files, key },
})

export const cloneResaleAuthorization =
  ({
    partnerId,
    productId,
    resaleAuthorizationState,
    crmOpportunityId,
    crmObjectLink,
    cloud,
    cloneId,
    handleNext,
  }: {
    partnerId: string
    productId: string
    resaleAuthorizationState: ResaleAuthorizationsResellerAgreements
    crmOpportunityId?: string
    crmObjectLink?: string
    cloud?: CloudMarketplace
    handleNext?: (id: string, crmOpportunityId?: string) => void
    cloneId?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(
      startLoading(LoadingTypes.UPDATE_FLYOUT_RESELLER_AUTHORIZATION)
    )
    try {
      const cloudMarketplace = cloud || 'AWS'
      const product =
        getState().productsListing[cloudMarketplace].products?.rows.find(
          product => product.productId === productId
        ) || ({} as Product)

      const payload = transformResaleAuthForSave({
        product,
        resaleAuthorizationState,
        crmOpportunityId,
        crmObjectLink,
        userProfile: getState().userProfile.userProfile,
        cloud,
      })
      const response = await cloneAPIresaleAuthorization({
        partnerId,
        resaleAuthorization: payload,
        saveAsDraft: true,
        cloud,
        cloneId,
        crmOpportunityId,
      })
      if (handleNext && response && response?.data) {
        const clonedResaleAuthorizationId =
          cloudMarketplace === 'AWS'
            ? camelize(response?.data).labraAwsResellerAgreementId
            : camelize(response?.data).mppoId
        const clonedCrmOpportunityId = camelize(response?.data).crmOpportunityId
        await handleNext(
          clonedResaleAuthorizationId,
          clonedCrmOpportunityId
        )
      }
      dispatch(
        updateAppAlert({
          message: cloneResaleAuthSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.UPDATE_FLYOUT_RESELLER_AUTHORIZATION)
      )
    }
  }

export const deactivateResaleAuthorization =
  ({
    partnerId,
    resellerAuthorizationId,
    awsAgreementId,
    handleNext,
    status,
  }: {
    partnerId: string
    resellerAuthorizationId?: string
    awsAgreementId: string
    handleNext?: () => void
    status?: ResellerAgreementStatus
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(
      startLoading(LoadingTypes.UPDATE_FLYOUT_RESELLER_AUTHORIZATION)
    )
    try {
      const dataToBeSent = {
        status: status || ResellerAgreementStatus.IN_PROGRESS,
        awsAgreementId,
      }
      const { data } = await patchResellerData(
        partnerId,
        resellerAuthorizationId as string,
        { ...dataToBeSent },
        false
      )
      if (handleNext && data) {
        await handleNext()
      }
      resellerAuthorizationId &&
        dispatch(
          getCurrentResaleAuthorization({
            partnerId,
            resaleAuthorizationId: resellerAuthorizationId,
            cloud: 'AWS',
          })
        )
      dispatch(
        updateAppAlert({
          message: deactivateResaleAuthSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.UPDATE_FLYOUT_RESELLER_AUTHORIZATION)
      )
    }
  }

export const submitResaleAuthorization =
  ({
    partnerId,
    productId,
    saveAsDraft,
    crmOpportunityId,
    crmObjectLink,
    submitCallBack,
    resaleAuthorization,
    labraNewUiRevamp = false,
    creatorData,
  }: {
    partnerId: string
    productId: string
    saveAsDraft?: boolean
    resaleAuthorization: ResaleAuthorizationWithErrors
    crmOpportunityId?: string
    crmObjectLink?: string
    submitCallBack?: (props: {
      id?: string
      isDraft?: boolean
      resaleAuthorization?: Record<string, unknown>
    }) => void
    labraNewUiRevamp?: boolean
    creatorData?: {
      creatorEmail?: string
      creatorName?: string
    }
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (saveAsDraft) {
      await dispatch(startLoading(LoadingTypes.DRAFT_SAVE_RESALE_AUTHORIZATION))
    } else {
      await dispatch(startLoading(LoadingTypes.SAVE_RESALE_AUTHORIZATION))
    }
    try {
      const cloudMarketplace = getState().productListingFilters.cloudMarketplace
      const product =
        getState().productsListing[cloudMarketplace].products?.rows.find(
          product => product.productId === productId
        ) || ({} as Product)

      const { userProfile } = getState().userProfile
      const { email, firstName } = userProfile
      const hasCreatorData = !isEmpty(firstName) && !isEmpty(email)
      const creatorDataToBeSent = creatorData
        ? snakeize({ creatorData: creatorData })
        : hasCreatorData
          ? {
              creator_data: { creator_name: firstName, creator_email: email },
            }
          : {}

      const sentPayload = getSubmitPayloadResaleAuthorization({
        cloud: cloudMarketplace,
        resaleAuthorization,
        product,
        userProfile: userProfile,
        crmOpportunityId,
        crmObjectLink,
        saveAsDraft,
        labraNewUiRevamp,
      })

      if (!resaleAuthorization?.id) {
        const response = await submitAPIresaleAuthorization({
          cloud: cloudMarketplace,
          resaleAuthorization: {
            ...sentPayload,
            ...(cloudMarketplace === 'AWS' ? creatorDataToBeSent : {}),
          },
          saveAsDraft,
          partnerId,
        })

        const createdId =
          cloudMarketplace === 'AWS'
            ? (response?.data as { labra_aws_reseller_agreement_id?: string })
                ?.labra_aws_reseller_agreement_id
            : (response?.data as { mppo_id?: string })?.mppo_id

        if (createdId) {
          await dispatch(
            updateResaleAuthorizationInCreation(
              { key: 'id', value: createdId },
              productId
            ) as unknown as AnyAction
          )
        }

        if (saveAsDraft) {
          await dispatch(updateNoOfTimesSaved(productId))
        }

        if (submitCallBack && response) {
          submitCallBack({
            id: createdId,
            isDraft: saveAsDraft,
          })
        }
      } else {
        await patchAPIresaleAuthorization({
          cloud: cloudMarketplace,
          resaleAuthorization: {
            ...sentPayload,
            ...(cloudMarketplace === 'AWS' ? creatorDataToBeSent : {}),
          },
          saveAsDraft,
          partnerId,
          resaleAuthorizationId: resaleAuthorization?.id,
        })

        if (!saveAsDraft) {
          await dispatch(updateHasBeenSent(true, productId))
        } else {
          await dispatch(updateNoOfTimesSaved(productId))
        }

        if (submitCallBack) {
          submitCallBack(
            !saveAsDraft
              ? {
                  id: resaleAuthorization?.id,
                  isDraft: saveAsDraft,
                }
              : {
                  resaleAuthorization: resaleAuthorization as unknown as Record<
                    string,
                    unknown
                  >,
                }
          )
        }
      }

      dispatch(
        updateAppAlert({
          message: saveAsDraft
            ? resaleAuthorizationSaveSuccess
            : resaleAuthorizationCreationSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )

      emitSalesforceResourceSaved({
        resourceType: 'reseller-auth',
        partnerId,
        opportunityId: crmOpportunityId || null,
        resellerAuthorization: {
          id: resaleAuthorization?.id,
          draft: !!saveAsDraft,
          hasBeenSent: !saveAsDraft,
          productId,
        },
      })
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (saveAsDraft) {
        await dispatch(
          stopLoading(LoadingTypes.DRAFT_SAVE_RESALE_AUTHORIZATION)
        )
      } else {
        await dispatch(stopLoading(LoadingTypes.SAVE_RESALE_AUTHORIZATION))
      }
    }
  }

export const getDefaultResaleAuthorizationInCreation =
  ({
    productId,
    crmOpportunityId,
    crmId,
    crmObjectId,
    templateId,
    accountId,
    crmType,
    cloudMarketplace: cloudMarketplaceOverride,
  }: {
    productId: string
    crmOpportunityId?: string
    crmId: string
    crmObjectId: string
    templateId?: string
    accountId?: string
    crmType?: CRMTypes
    cloudMarketplace?: CloudMarketplace
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GET_DEFAULT_OFFERS))
    try {
      await dispatch(clearResaleAuthorizationInCreation(productId))
      const cloudMarketplace = (
        cloudMarketplaceOverride ||
        getState().productListingFilters.cloudMarketplace
      ).toUpperCase() as CloudMarketplace
      const product =
        getState().productsListing[cloudMarketplace].products.rows.find(
          product => product.productId === productId
        ) || ({} as Product)
      const mappingTemplate = getState().marketplaceMappingManager.templateData
      const userProfile = getState().userProfile.userProfile
      const resellers = getState().resellers.resellers

      const { data } = await getMarketplaceDefaultOfferMarketplaceMapping(
        crmId,
        templateId,
        accountId,
        crmObjectId
      )

      let extractedData = {}
      let fieldMetadata = {}

      const shouldPopulateFromMappingTemplate =
        crmType === CRMTypes.salesforce ||
        (crmType === CRMTypes.hubspot && cloudMarketplace === 'AZURE')

      if (shouldPopulateFromMappingTemplate) {
        const referralObject = data?.referral_object
        const selectedTemplate = mappingTemplate?.templateData?.fieldMappings
        const dimensions = data?.dimensions
        extractedData = mappingReferralObjectToForm(
          productId,
          referralObject,
          dimensions,
          mappingTemplate,
          product,
          cloudMarketplace,
          resellers
        )
        fieldMetadata = getFieldMetadataFromTemplate(
          selectedTemplate,
          referralObject,
          cloudMarketplace
        )
      }

      let offerData = {}

      offerData = {
        ...getInitialResaleAuthorizationData({
          product,
          cloud: cloudMarketplace,
          crmOpportunityId,
          userProfile,
          getState,
          labraNewUiRevamp: true,
        }),
        ...extractedData,
      }

      if (cloudMarketplace === 'AWS') {
        const pricesArray: string[] = getPricesArray(
          product?.productDimensions as Dimension[]
        )

        offerData = {
          ...camelize(offerData),
          pricesArray,
          contract_duration_time_unit: 'MONTHS',
        }
      } else {
        offerData = {
          ...camelize(offerData),
        }
      }
      dispatch(
        setResaleAuthorizationInCreation({
          productId,
          data: { ...offerData },
          errors: {},
          noOfTimesSaved: 0,
        })
      )
      await dispatch(
        setFieldMetaData(productId, camelize({ ...fieldMetadata }))
      )
      return true
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.GET_DEFAULT_OFFERS))
    }
  }

export const setFieldMetaData = (
  productId: string,
  fieldMetadata: FieldMetadata
) => ({
  type: ResaleAuthorizationCreationActionTypes.SET_FIELD_METADATA,
  payload: { productId, fieldMetadata },
})
